import "./LinkPages.scss";
import { useEffect, useState } from "react";
import LinkPagesApi, {getLpBasePath} from "../../api/LinkPagesApi";
import { LinkPageDto } from "../../domain/LinkPageDto";
import { Button, Skeleton } from "antd";
import LinkPageCard from "../link-pages/LinkPageCard";
import { DeletePageModal } from "../link-pages/DeletePageModal";
import { env } from "../../env/env";
import { ChangePageLocationModal } from "../design/ChangePageLocationModal";
import { useRecoilValue } from "recoil";
import { memberState } from "../../state/atoms";
import { LocationDto } from "../../generated/dashboard-client";
import SearchCategories from "../SearchCategories";
import { isProdEnv } from "../../util/constants";
import { GlobalOutlined, StarOutlined } from "@ant-design/icons";
import LinkPagesIcon from "../icons/LinkPagesIcon";
import { isDesignSubscription, isProSubscription } from "../../service/BillingService";

interface LinkPagesProps {
  activeLocation: LocationDto;
}

export default function LinkPages({activeLocation}: LinkPagesProps) {
  const member = useRecoilValue(memberState);

  const [pages, setPages] = useState([] as LinkPageDto[]);
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(null as LinkPageDto | null);
  const [isRemoveModal, setRemoveModal] = useState(false);
  const [isChangeLocationModal, setChangeLocationModal] = useState(false);

  const [prevLocation, setPrevLocation] = useState({} as LocationDto);

  useEffect(() => {
    if (!activeLocation || !activeLocation.internalId) return;

    setPrevLocation(activeLocation);

    if (activeLocation.id !== prevLocation.id) {
      setLoading(true);
      LinkPagesApi.getPages(activeLocation.internalId)
      .then((resp) => {
        let sortedLps = resp.data.sort(function(a, b) {
          return b.updatedDate - a.updatedDate
        });
        setPages(sortedLps);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
    }
  }, [activeLocation, member.plan, prevLocation.id]);

  function onDeleteClick(page: LinkPageDto) {
    setActivePage(page);
    setRemoveModal(true);
  }

  function onChangeLocation(page: LinkPageDto) {
    setActivePage(page);
    setChangeLocationModal(true);
  }

  function onRemoveModalClose(removed: boolean) {
    setRemoveModal(false);
    if (removed) {
      setPages(pages.filter((it) => it.id !== activePage?.id));
    }
  }

  function onChangeLocationClose(id: string, saved: boolean) {
    setChangeLocationModal(false);
    if (saved) {
      setPages(pages.filter((it) => it.id !== id));
    }
  }

  function goToNewLp() {
    if (!isProSubscription(member) && pages.length > 0) {
      goPro();
    } else {
      const locationId = activeLocation.internalId;
      const tkn = window.localStorage.getItem("tkn");
      window.location.href = `${getLpBasePath()}${env.lpContextPath}${isProdEnv ? '/lp' : ''}/app/new/links?businessId=${locationId}&tkn=${tkn}`;
    }
  }

  function goPro() {
    if (isDesignSubscription(member)) {
      window.MHM.ProModal.switchToProWithOverview({ memberId: member.id, onSuccess: () => window.location.reload() });
    } else {
      window.MHM.ProModal.upgrade({ memberId: member.id, onSuccess: () => window.location.reload() });
    }
  }

  return (
    <div className={"link-pages"}>
      <div className="mobile-search-cats">
        <SearchCategories/>
      </div>
      {loading && <Skeleton active />}

      {!loading && pages.length > 0 && isProSubscription(member) && (
        <section>
          <header>
            <h3><LinkPagesIcon/> Link Pages</h3>
            <Button size={"large"} className={"primary"} onClick={goToNewLp}>
              <span className="desktop-message">+ New Link Page</span>
              <span className="mobile-message">+ New</span>
            </Button>
          </header>

          <ul>
            {pages.map((page) => (
              <LinkPageCard
                activeLocation={activeLocation}
                page={page}
                onDeleteClick={onDeleteClick}
                onChangeLocationClick={onChangeLocation}
              />
            ))}
          </ul>
        </section>
      )}

      {!loading && isProSubscription(member) && pages.length === 0 && (
        <section className={"splash"}>
          <div className={"data"}>
            <h3><LinkPagesIcon/> Link Pages</h3>
            <Button size={"large"} className={"primary"} onClick={goToNewLp}>
              Get Started
            </Button>

            <div className={"img img-mobile"}>
              <img
                loading="lazy"
                src={
                  "https://www.musthavemenus.com/imageservice/images/img/13/mymenus/LP-Tab-Tablet+Mobile.png"
                }
                alt={"Landing Page"}
              />
            </div>

            <ul>
              <li>
                <img
                  loading="lazy"
                  src={
                    "https://www.musthavemenus.com/imageservice/images/img/13/mymenus/link-icon.svg"
                  }
                  alt={"Link Icon"}
                />
                <div className={"dt"}>
                  <h5>Link to Everything</h5>
                  <p>
                    Gather all your most important links in one easy spot.
                    Connect it to a QR code for the most simple mobile
                    experience possible.
                  </p>
                </div>
              </li>
              <li>
                <img
                  loading="lazy"
                  src={
                    "https://www.musthavemenus.com/imageservice/images/img/13/mymenus/cursor-icon.svg"
                  }
                  alt={"Cursor Icon"}
                />
                <div className={"dt"}>
                  <h5>See What's Clicking</h5>
                  <p>
                    Track which links your customers click on the most. See what
                    works and what doesn't with incisive analytics.
                  </p>
                </div>
              </li>
              <li>
                <img
                  loading="lazy"
                  src={
                    "https://www.musthavemenus.com/imageservice/images/img/13/mymenus/lightning-icon.svg"
                  }
                  alt={"Lightning Icon"}
                />
                <div className={"dt"}>
                  <h5>Swap Content on the Fly</h5>
                  <p>
                    Everybody loves speed and ease of use. Our user-friendly
                    interface lets you make changes fast.
                  </p>
                </div>
              </li>
            </ul>
          </div>
          <div className={"img img-desktop"}>
            <img
              loading="lazy"
              src={
                "https://www.musthavemenus.com/imageservice/images/img/13/mymenus/LP-Tab-Desktop.png"
              }
              alt={"Landing Page"}
            />
          </div>
        </section>
      )}

      {!loading && !isProSubscription(member) &&
      <section className="freemium-splash">
        <div className="upgrade-txt">
          <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>
          <h4>Upgrade to use this feature</h4>
        </div>
        <h2 className="mobile-only">
          <LinkPagesIcon/> Link Pages
        </h2>
        <div className="info">
          <div className="text">
            <h2 className="desktop-only">
              <LinkPagesIcon/> Link Pages
            </h2>
            <ul>
              <li>
                <div>
                  <GlobalOutlined/>
                </div>
                <div>
                  <h4>Mini websites</h4>
                  <p>Link pages are like a mini website you can make in minutes. Link to promos, event info, maps, menus, digital storefronts and more.</p>
                </div>
              </li>
              <li>
                <div>
                  <img src="https://timber.mhmcdn.com/site/marketing/icons/QRCode.svg" alt="qr"/>
                </div>
                <div>
                  <h4>Easy access</h4>
                  <p>Link pages provide a unique URL that customers can access from a QR code, social post, email and more.</p>
                </div>
              </li>
              <li>
                <div>
                  <StarOutlined/>
                </div>
                <div>
                  <h4>Create the look</h4>
                  <p>Choose background images and colors to match your link page to your brand, style or season.</p>
                </div>
              </li>
              <li>
                <div>
                  <img src="https://timber.mhmcdn.com/site/marketing/icons/Megaphone.svg" alt="megaphone"/>
                </div>
                <div>
                  <h4>Start marketing in minutes!</h4>
                  <p>No special skills or coding required. You'll have a great marketing tool ready-to-go in minutes.</p>
                </div>
              </li>
            </ul>
            <Button size={"large"} className={"primary"} onClick={goPro}>
              Get Pro to use Link Pages!
            </Button>
          </div>
          <div className="img">
            <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/LPFreemiumSplashHero2.webp" alt="lp freemium hero"/>
          </div>
        </div>
        <div className="examples">
          <h3>More amazing customer examples</h3>
          <ul>
            <li>
              <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/lp_americanstar.webp" alt="lp"/>
            </li>
            <li>
              <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/lp_bonapetit.webp" alt="lp"/>
            </li>
            <li>
              <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/lp_cabana.webp" alt="lp"/>
            </li>
            <li>
              <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/lp_upstairsonthebay.webp" alt="lp"/>
            </li>
            <li>
              <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/lp_woodbridge.webp" alt="lp"/>
            </li>
            <li>
              <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/lp_countryclubwaterbury.webp" alt="lp"/>
            </li>
            <li>
              <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/lp_cloverleaf.webp" alt="lp"/>
            </li>
            <li>
              <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/lp_snowymountain.webp" alt="lp"/>
            </li>
            <li>
              <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/lp_middleroadinn.webp" alt="lp"/>
            </li>
            <li>
              <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/lp_thegolfclub.webp" alt="lp"/>
            </li>
            <li>
              <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/lp_namak.webp" alt="lp"/>
            </li>
            <li>
              <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/lp_orleans.webp" alt="lp"/>
            </li>
            <li>
              <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/lp_hamburgermarys.webp" alt="lp"/>
            </li>
            <li>
              <img src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/LPSplash/lp_bullcreek.webp" alt="lp"/>
            </li>
          </ul>
        </div>
      </section> 
      }

      <DeletePageModal
        page={activePage}
        isVisible={isRemoveModal}
        onClose={onRemoveModalClose}
      />

      <ChangePageLocationModal
        isVisible={isChangeLocationModal}
        id={activePage?.id ?? ""}
        onClose={onChangeLocationClose}
      />
    </div>
  );
}
